import Taro from '@tarojs/taro';
import Config from '@/config/path';
import { reportAndLog } from '@/utils/log';
import * as Sentry from '@sentry/browser';
import { getStorageSync, checkAppClient, setLoginReturnUrl, callAppLogin } from './index';

export enum RequestCode {
  CODE_SUCCESS = 200,
  CODE_UNAUTHORIZED = 401
}

// 不拦截401接口的URL
export const excludeAuthorizedUrl: string[] = [
  '/video/product/record/list',
  '/member/product/index/list',
  '/index/coupon/show'
];

// 不弹错误信息
export const excludeErrorMessag: string[] = ['/trade/order/cancel-check'];

/**
 * 简易封装网络请求
 * @param {*} options
 *
 */
let hasToLogin = false; //防止多个接口同时403导致存储的上一个路由栈是登陆页路劲
export async function request(options: any) {
  const { url, payload, method = 'GET' } = options;
  const env = /^\/reverse_geocoding\/v3$/.test(url) ? 'baidu' : H5_ENV;
  // isLog：是否为埋点请求
  const baseUrl: string = options.isLog ? Config.LogDomain[env] : Config.Domain[env];

  const reqUrl = `${baseUrl}${url}`;

  if (checkAppClient().isAndroid) {
    window.token = window.srightNativeBridge.getToken();
  }
  if (checkAppClient().isIos) {
    window.token = window.prompt('getToken', '');
  }

  const header: any = {
    'caucen-token':
      checkAppClient().isAndroid || checkAppClient().isIos
        ? window.token
        : getStorageSync('id_token'),
    'client-source': 1,
    'os-name': 'h5',
    'x-city-code': getStorageSync('h5-cityCode') || '330100'
  };
  header['content-type'] = options.contentType || 'application/json';
  return Taro.request({
    url: reqUrl,
    method,
    data: payload,
    header,
    responseType: options.responseType || 'text'
  })
    .then(async (res) => {
      if (options.isDownload) {
        return res.data;
      }

      const { code, message } = res.data;

      //  正常返回状态码
      if (code) {
        if (options.handler) {
          options.handler(res.data);
        }

        if (code === RequestCode.CODE_SUCCESS) {
          // 200正常请求
          return res.data;
        } else {
          if (!excludeErrorMessag.includes(url)) {
            Taro.showToast({
              title: message || '接口请求异常',
              icon: 'none'
            });
          }

          Sentry.captureException(new Error(`接口请求异常 ${reqUrl}`), {
            extra: {
              url: reqUrl,
              data: res.data,
              code,
              message
            }
          });

          return false;
        }
      } else {
        // 返回http协议消息体
        try {
          const { body, result, status } = res.data;
          if (body && body?.protocolData.status === RequestCode.CODE_UNAUTHORIZED) {
            // app环境内调用app登录
            if (callAppLogin()) {
              return;
            }
            Taro.removeStorageSync('id_token');
            // 401未授权
            if (!excludeAuthorizedUrl.includes(url)) {
              // 不拦截搜索历史记录接口
              if (hasToLogin) return;
              setLoginReturnUrl();
              hasToLogin = true;
              setTimeout(() => {
                // 设置return_url
                Taro.redirectTo({
                  // 返回到登陆
                  url: `/pages/login`
                });
                hasToLogin = false;
              }, 500);
            }
          } else if (result && status === 0) {
            // 针对百度地图api单独处理
            return result;
          }
        } catch (error) {
          // app环境内调用app登录
          if (callAppLogin()) {
            return;
          }
          setTimeout(() => {
            Taro.navigateTo({
              // 返回到登陆
              url: `/pages/login`
            });
            // 设置return_url
            setLoginReturnUrl();
          }, 500);
        }
      }
    })
    .catch((err) => {
      const errorInfo = {
        url: err.url,
        status: err.status,
        type: err.type,
        message: '请求出错'
      };
      reportAndLog(JSON.stringify(errorInfo), -1);
      return Promise.reject({ ...err });
    });
}

export function getApi(url: string, params: any = {}, options: any = {}) {
  const { handler, ...payload } = params;
  if (handler) {
    options.handler = handler;
  }

  return request({
    url,
    method: 'GET',
    payload,
    ...options
  });
}

export function postApi(url: string, params: any = {}, options: any = {}) {
  const { handler, ...payload } = params;
  if (handler) {
    options.handler = handler;
  }

  return request({
    url,
    method: 'POST',
    payload,
    ...options
  });
}

export function postForm(url: string, params: any = {}, options: any = {}) {
  const { handler, ...payload } = params;

  if (handler) {
    options.handler = handler;
  }

  return request({
    url,
    method: 'POST',
    payload,
    contentType: 'multipart/form-data',
    ...options
  });
}

/** 处理下载请求 */
export function downloadApi(url: string, params: any = {}, options: any = {}) {
  const { handler, ...payload } = params;
  if (handler) {
    options.handler = handler;
  }

  return request({
    url,
    method: 'GET',
    payload,
    responseType: 'arraybuffer',
    isDownload: true,
    contentType: 'application/octet-stream',
    ...options
  });
}
